import { applyMiddleware, compose, createStore } from "redux";
import reduxImmutableStateInvariant from "redux-immutable-state-invariant";
import thunk from "redux-thunk";
import { isProduction } from "@claroty/utilities/infra/environmentUtils";
import { getHistory } from "@claroty/utilities/store/history";
import { routerMiddleware } from "connected-react-router";
import * as devFlags from "../utils/infra/devFlags";
import { isAnalyticsDisabled } from "../utils/tracking/isAnalyticsDisabled";
import analyticsMiddleware from "./analytics/analytics";

const g_thunk_arg = { api: undefined };
export const setThunkAPI = api => (g_thunk_arg.api = api);

const reactRouterMiddleware = routerMiddleware(getHistory());

const getCommonMiddleware = () => [
    // thunk middleware can also accept an extra argument to be passed to each thunk action
    // https://github.com/gaearon/redux-thunk#injecting-a-custom-argument
    thunk.withExtraArgument(g_thunk_arg),
    reactRouterMiddleware,
];

function createStoreProd(reducer, initialState) {
    let middleware = [];
    if (!isAnalyticsDisabled()) {
        middleware.push(analyticsMiddleware);
    }
    middleware = middleware.concat(getCommonMiddleware());
    return createStore(reducer, initialState, compose(applyMiddleware(...middleware)));
}

function createStoreDev(reducer, initialState) {
    const supportReduxDevTools = localStorage[devFlags.REDUX_DEV_TOOLS] === "true";
    const reduxConsoleLogger = localStorage[devFlags.REDUX_CONSOLE_LOG] || "";

    const middleware = [].concat(getCommonMiddleware());

    if (reduxConsoleLogger) {
        const logger = store => next => action => {
            if (!action.type.includes(reduxConsoleLogger) && reduxConsoleLogger != "any") return next(action);
            // eslint-disable-next-line no-console
            console.log("dispatching", action);
            const result = next(action);
            // eslint-disable-next-line no-console
            console.log("next state", store.getState());
            return result;
        };
        middleware.push(logger);
    }

    // Redux middleware that spits an error on you when you try to mutate your state either inside a
    // dispatch or between dispatches.
    if (supportReduxDevTools) {
        middleware.push(reduxImmutableStateInvariant());
    }

    // add support for Redux dev tools
    const composeEnhancers =
        (supportReduxDevTools ? (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ : undefined) || compose;
    const store = createStore(reducer, initialState, composeEnhancers(applyMiddleware(...middleware)));

    return store;
}

export default isProduction ? createStoreProd : createStoreDev;
